/** Circumvents CORS restrictions when loading a web worker from a different domain.
 * For workers, same-origin policy is always strictly enforced and cannot be circumvented.
 * Using a blob URL is a widely used workaround.
 *
 * Supports both classic workers (importScripts) and module workers (dynamic import).
 */
export class CorsWorker {
    private readonly worker: Worker
    private readonly objectURL: string

    constructor(url: string | URL, workerOptions?: WorkerOptions) {
        const urlString = JSON.stringify(url.toString())
        const isModule = workerOptions?.type === "module"
        // Module workers: dynamic import() is async. We must signal readiness after
        // the module loads, so the main thread can buffer postMessage calls until then.
        const blobContent = isModule
            ? `import(${urlString}).then(() => postMessage({__workerReady: true})).catch(e => { console.error("[CorsWorker] import failed:", e); });`
            : `importScripts(${urlString});`
        this.objectURL = URL.createObjectURL(
            new Blob([blobContent], {
                type: "application/javascript",
            }),
        )
        this.worker = new Worker(this.objectURL, workerOptions)
        // The blob URL is NOT revoked here. For module workers, the browser loads
        // the blob content asynchronously. Revoking before the async load completes
        // causes ERR_FILE_NOT_FOUND. Instead, revokeBlobUrl() should be called when
        // the worker is terminated.
    }

    getWorker() {
        return this.worker
    }

    revokeBlobUrl() {
        URL.revokeObjectURL(this.objectURL)
    }
}
